import { FC, useCallback, useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useSelector } from "react-redux";
import { completeWorkItems } from "../../redux/apis/blenderApiSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectAuth } from "../../redux/slices/authSlice";
import { toggleTaskManagerOpen } from "../../redux/slices/consolePageSlice";

interface IProps {}

const RootContainer = styled.div`
  height: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
  background-color: ${(props) => props.theme.neutral};
  box-shadow: 20px 20px 25px -15px rgb(0 0 0 / 0.1),
    10px 8px 10px -8px rgb(0 0 0 / 0.1);
`;

const TaskManagerContainer: FC<IProps> = () => {
  const dispatch = useAppDispatch();
  const { workItems } = useAppSelector(
    (state) => state.blenderState.agentClientState
  );
  const { selectedWorkItemId } = useAppSelector(
    (state) => state.consolePageState
  );

  const { token } = useSelector(selectAuth);

  const [dispositionCode, setDispositionCode] = useState("");
  const [note, setNote] = useState("");

  const dispositionCodes = useMemo(() => {
    if (!workItems || !workItems[selectedWorkItemId]) return <></>;
    return Object.values(workItems[selectedWorkItemId].usableClosureCodes).map(
      (dispositionCode) => (
        <option key={dispositionCode} style={{ fontWeight: 600 }}>
          {dispositionCode}
        </option>
      )
    );
  }, [selectedWorkItemId, workItems]);

  useEffect(() => {
    if (!workItems || !workItems[selectedWorkItemId]) {
      setDispositionCode("");
    } else if (
      workItems[selectedWorkItemId].usableClosureCodes !== undefined &&
      workItems[selectedWorkItemId].usableClosureCodes != null &&
      workItems[selectedWorkItemId].usableClosureCodes.length > 0
    ) {
      setDispositionCode(workItems[selectedWorkItemId].usableClosureCodes[0]); //set default disposition code to first one in the list
    }
  }, [workItems, selectedWorkItemId]);

  const isInputDisabled = useMemo(() => {
    if (!workItems || !workItems[selectedWorkItemId]) return true;
    if (workItems[selectedWorkItemId].workItemState === "Afterwork")
      return false;
    return true;
  }, [selectedWorkItemId, workItems]);

  const isButtonDisabled = useMemo(() => {
    if (!isInputDisabled && dispositionCode) return false;
    return true;
  }, [dispositionCode, isInputDisabled]);

  const closeTaskHandler = useCallback(() => {
    dispatch(
      completeWorkItems(
        {
          workItemId: selectedWorkItemId,
          notes: note,
          closureCode: dispositionCode,
        },
        token
      )
    );
    setNote("");
    dispatch(toggleTaskManagerOpen());
  }, [dispatch, dispositionCode, note, selectedWorkItemId, token]);

  return (
    <RootContainer>
      <Form.Label
        htmlFor="tsk_mngr_disposition_code_select"
        style={{ fontWeight: 600 }}
        className="mb-3"
      >
        Set disposition code
      </Form.Label>
      <Form.Select
        id="tsk_mngr_disposition_code_select"
        disabled={isInputDisabled}
        className="mb-2"
        value={dispositionCode}
        onChange={(e) => setDispositionCode(e.target.value)}
      >
        {dispositionCodes}
      </Form.Select>
      <Form.Control
        type="text"
        id="tsk_mngr_note"
        placeholder="note"
        disabled={isInputDisabled}
        className="mb-4"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button
        id="tsk_mngr_close_task_btn"
        variant="dark"
        disabled={isButtonDisabled}
        onClick={closeTaskHandler}
      >
        Close task
      </Button>
    </RootContainer>
  );
};

export default TaskManagerContainer;
